import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { RasaContext } from 'context'
import { HeaderComponent } from 'components/header/component'
import { Dataset } from 'generic/dataset'
import * as GenericRedux from 'generic/genericRedux'
import { RasaReactComponent } from 'generic/rasaReactComponent'
import * as React from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Card, CardBody, CardHeader, Input } from 'reactstrap'
import { DropdownComponent, MultiSelectDropdown, OnChangeEvent } from 'components/dropdown/component'
import { PromptTemplateEditor } from './PromptTemplateEditor'
import { DraggableTemplateList } from './DraggableTemplateList'
import { ContextBuilder } from './ContextBuilder'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'

import './_styles.scss'

interface PromptStats {
  model: string,
  type: string,
  count: number,
}

interface PromptTemplate {
  id: number,
  sequence: number,
  prompt: string,
  options: string,
}

interface EditablePromptTemplate extends PromptTemplate {
  changedFields: string[],
  invalidFields: string[],
  saveState?: 'idle' | 'saving' | 'saved' | 'error',
  saveError?: string,
}

interface ChatPayload {
  // Required fields
  aiPromptType: string
  emojis: number
  language: string
  length: number
  prompt: string
  promptType: string
  tone: string

  // Optional content fields
  content?: string
  title?: string
  url?: string
  topics?: string[]

  // Advanced options
  includeBackground?: boolean

  // Array structures for repeat sections
  articles?: { title: string; text: string }[]
  categories?: { name: string; keywords: string }[]
  subjects?: { title: string }[]
  background?: {
    summary: string
    colors?: {
      primary: string
      secondary: string
    }
  }
}

interface Token {
  key: string
  value: string
}

interface AIEngine {
  id: number,
  is_default?: boolean,
  engine_name: string,
  engine_version: string,
  templates: PromptTemplate[],
}

interface PromptType {
  engines: AIEngine[],
  id: number,
  name: string,
  description: string,
  defaultEngine: AIEngine,
}

const NO_ENGINE: AIEngine = {
  id: -1,
  engine_name: '',
  engine_version: '',
  is_default: false,
  templates: [],
}

interface EditablePromptType extends PromptType {
  isDirty?: boolean,
}

const NO_PROMPT_TYPE: PromptType = {
  engines: [ NO_ENGINE ],
  id: -1,
  name: '',
  description: '',
  defaultEngine: NO_ENGINE
}

interface TestHistoryEntry {
  id: string
  timestamp: string
  payload: ChatPayload
  context?: any
  tokens?: Token[]
  response?: any
  error?: string
  templateType: 'nunjucks' | 'token' | null
}

interface State {
  identifier: string,
  communityId: number,
  editPrompt: EditablePromptType,
  editableTemplates: EditablePromptTemplate[],
  editingTemplates: boolean,
  editingTemplate: boolean,
  showingFullPrompt: boolean,
  showingTestPanel: boolean,
  showingTemplateManager: boolean,
  testPayload: ChatPayload | null,
  testContext: any,
  testTokens: Token[],
  testLoading: boolean,
  testResponse: any,
  testError: string | null,
  testHistory: TestHistoryEntry[],
  payloadBuilderMode: string,
  customPayload: any,
  engines: AIEngine[],
  promptTypes: PromptType[]
  selectedPrompt: number,
  selectedEngine: number,
  selectedSequence: number,
  stats: PromptStats[]
}

const emptyState: State = {
  identifier: '',
  communityId: -1,
  editableTemplates: [],
  editingTemplates: false,
  editingTemplate: false,
  showingFullPrompt: false,
  showingTestPanel: false,
  showingTemplateManager: false,
  testPayload: null,
  testContext: {},
  testTokens: [],
  testLoading: false,
  testResponse: null,
  testError: null,
  testHistory: [],
  payloadBuilderMode: '',
  customPayload: {},
  editPrompt: NO_PROMPT_TYPE,
  engines: [],
  promptTypes: [],
  selectedPrompt: -1,
  selectedEngine: -1,
  selectedSequence: -1,
  stats: [],
}

class RexPromptsComponent extends RasaReactComponent<any, State>{
  public static contextType = RasaContext;

  constructor(props: any) {
    super(props, "rexPrompts", emptyState)
  }

  public componentDidMount = () => {
    this.context.user.init().then(({person, activeCommunity}) => {
      this.setState({
        identifier: activeCommunity.identifier,
        communityId: activeCommunity.communityId,
      }, () => {
        const promptTypesPromise = new Dataset().loadCommunityDataset(
          'aiPromptTypes',
          activeCommunity.communityId,
          []
        )
        const promptStatsPromise = new Dataset().loadCommunityDataset(
          'aiPromptStats',
          activeCommunity.communityId,
          []
        )
        Promise.all([promptTypesPromise, promptStatsPromise])
          .then(([promptTypeResults, promptStatsResults]) => {
          this.setState({
            engines: promptTypeResults.engines.map((e) => ({
              ...e,
              engine_name: e.name,
              engine_version: e.version,
            })),
            promptTypes: promptTypeResults.prompts.map((prompt) => ({
              ...prompt,
              defaultEngine: prompt.engines.find((e) => e.is_default) || {}
            })).sort((a, b) => a.name.localeCompare(b.name)),
            stats: promptStatsResults[0].sort((a, b) => b.count - a.count),
          })
        })
      })
    })
  }

  public render = () => {
    return <div className="rex-ai-prompts">
      <HeaderComponent
        icon="t-rex"
        title={'AI Prompts'}
        subTitle='Manage the AI Prompts'
        description={[
          'View and edit AI prompts used to guide t-rex.',
        ]}
      />
      <Row>
        { this.promptTypesTable()}
      </Row>
      <Row>
        { this.promptStatsTable()}
      </Row>
      {this.promptsModal()}
      {this.enginesModal()}
    </div>
  }

  private promptStatsTable = () => {
    if ( this.state.stats.length === 0 ) {
      return null
    }
    return <div>
      <h4>Prompt Stats</h4>
      <Grid data={this.state.stats}>
        <GridColumn field="type" title="Prompt Type" />
        <GridColumn field="model" title="Model" />
        <GridColumn field="count" title = "Count" />
      </Grid>
    </div>
  }

  private promptTypesTable = () => {
    if ( this.state.promptTypes.length === 0 ) {
      return null
    }
    return <div>
      <h4>Prompt Types</h4>
      <Grid data={this.state.promptTypes}>
        <GridColumn field="name" title="Prompt Type" />
        <GridColumn field="description" title="Description" />
        <GridColumn title="Primary" cell={this.primaryEngineCell}/>
        <GridColumn title="Alternates" cell={this.alternateEnginesCell}/>
        <GridColumn title="Edit" cell={this.editPromptCell}/>
        <GridColumn title="Prompts" cell={this.viewPromptsCell}/>
      </Grid>
    </div>
  }

  private engineLabel = (
    engine: AIEngine, showDefault: boolean
  ): string  => {
    const labelParts = []
    if (showDefault && engine.is_default) {
      labelParts.push('*')
    }

    labelParts.push(engine.engine_name)
    labelParts.push(engine.engine_version)
    return labelParts.join(' ')
  }

  private primaryEngineCell = (props: any) => {
    return <td>
      <span>{this.engineLabel(props.dataItem, false)}</span>
    </td>
  }

  private clonePrompt = (prompt: PromptType) => {
    return {
      ...prompt,
      engines: prompt.engines.map((engine) => {
        return {
          ...engine
        }
      })
    }
  }

  private alternateEnginesCell = (props: any) => {
    const secondaryEngines = props.dataItem.engines.filter((engine: AIEngine) => {
      return engine.engine_name !== props.dataItem.engine_name ||
             engine.engine_version !== props.dataItem.engine_version
    })
    return <td>
        <span>{secondaryEngines.map((e) => this.engineLabel(e, false)).join(',')}</span>
      </td>
  }

  private viewPromptsCell = (props: any) => {
    return <td>
      <Button onClick={() => {
        const templates = props.dataItem.engines[0].templates || []
        this.setState({
          selectedPrompt: props.dataItem.id,
          selectedEngine: props.dataItem.engines[0].id,
          selectedSequence: templates.length > 0 ? templates[0].sequence : -1,
          editableTemplates: this.toEditableTemplates(templates)
        })
      }}>
        View 
      </Button>
    </td>
  }

  private editPromptCell = (props: any) => {
    return <td>
      <Button onClick={() => this.setState({
        editPrompt: this.clonePrompt(props.dataItem),
      })}>
        Edit 
      </Button>
    </td>
  }

  protected toEditableTemplates = (templates: PromptTemplate[]): EditablePromptTemplate[] => {
    return templates.map((t) => {
      return {
        ...t,
        invalidFields: [],
        changedFields: [],
        saveState: 'idle',
        saveError: undefined
      }
    })
  }

  private engineItem = (engine: AIEngine) => ({
    value: engine.id,
    label: this.engineLabel(engine, false)
  })

  protected enginesModal = () => {
    return <div className="ai-prompt-modal">
        <Modal isOpen={this.state.editPrompt.id >= 0} className="ai-prompt-modal">
          <ModalBody>
            <Row>
              <h3>{this.state.editPrompt.name}</h3>
            </Row>
            <Row className="engines">
              Available:
              <MultiSelectDropdown
                   className="engines"
                   placeholder="Engines"
                   selected={this.state.editPrompt.engines.map((e) => this.engineItem(e))}
                   options={this.state.engines.map((engine: AIEngine) => this.engineItem(engine))}
                   onSelect={(selectedItems: any[]) => {
                    this.setState({
                      editPrompt: {
                        ...this.state.editPrompt,
                       isDirty: true,
                        engines: selectedItems.map((i) => i.value).map((id: number) => this.state.engines.find((engine: AIEngine) => engine.id === id))
                      }
                    })
                   }}
                 />
            </Row>
            <Row>
              Default:
              <DropdownComponent className="engines"
                data={this.state.editPrompt.engines.map((e: AIEngine) => {
                  return {
                    description: this.engineLabel(e, true),
                    key: e.id 
                  }
                })}
                selected={this.state.editPrompt.defaultEngine.id}
                onChange={(event: OnChangeEvent) => {
                  this.setState({
                    editPrompt: {
                      ...this.state.editPrompt,
                      isDirty: true,
                      defaultEngine: this.state.editPrompt.engines.find((engine) => engine.id === event.selected.key)
                    }
                  })
                }} />
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button onClick={this.savePrompt} disabled={!this.state.editPrompt.isDirty}>
              Save
            </Button>
            <Button onClick={() => this.setState({ 
              editPrompt: NO_PROMPT_TYPE
            })}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
    </div>
  }

  protected promptsModal = () => {

    const selectedPrompt = this.state.promptTypes.filter((p: PromptType) => p.id === this.state.selectedPrompt)[0] || NO_PROMPT_TYPE
    const selectedEngine = selectedPrompt.engines.filter((e: AIEngine) => e.id === this.state.selectedEngine)[0] || selectedPrompt.engines[0]

    return <div className="ai-prompt-modal">
        <Modal isOpen={this.state.selectedPrompt >= 0} className="ai-prompt-modal" size="xl">
          <ModalHeader toggle={() => this.setState({ 
            selectedPrompt: -1, 
            selectedEngine: -1, 
            selectedSequence: -1,
            editableTemplates: [],  
            editingTemplates: false,
            editingTemplate: false,
            showingFullPrompt: false,
            showingTestPanel: false,
            showingTemplateManager: false,
            testPayload: null,
            testContext: {},
            testTokens: [],
            testResponse: null,
            testError: null,
            testLoading: false
          })}>
            <div>
              <h4 className="mb-1">{selectedPrompt.name}</h4>
              <small className="text-muted">{selectedPrompt.description}</small>
            </div>
          </ModalHeader>
          <ModalBody>
            <Row className="mb-3 align-items-center">
              <Col md={2}>
                <label className="form-label mb-1">Engine:</label>
                <div className="engine-label">{this.engineLabel(selectedEngine, false)}</div>
              </Col>
              <Col md={2}>
                <label className="form-label mb-1">Template ID:</label>
                <div className="text-muted">
                  {selectedEngine.templates && selectedEngine.templates.length > 0 && this.state.selectedSequence >= 0 && (
                    (() => {
                      const selectedTemplate = selectedEngine.templates.find(t => t.sequence === this.state.selectedSequence)
                      return selectedTemplate ? `ID: ${selectedTemplate.id}` : 'No template selected'
                    })()
                  )}
                </div>
              </Col>
              <Col md={8} className="text-right">
                {selectedEngine.templates && selectedEngine.templates.length > 0 && this.state.selectedSequence >= 0 && (
                  (() => {
                    const selectedTemplate = selectedEngine.templates.find(t => t.sequence === this.state.selectedSequence)
                    return selectedTemplate ? (
                      <div className="btn-group" role="group">
                        {!this.state.editingTemplate ? (
                          <Button 
                            size="sm" 
                            color="primary" 
                            onClick={() => this.setState({ editingTemplate: true })}
                          >
                            <i className="fa fa-edit"></i> Edit
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            color="secondary" 
                            onClick={() => this.setState({ editingTemplate: false })}
                          >
                            <i className="fa fa-eye"></i> View
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          color="success" 
                          onClick={() => this.setState({ showingTestPanel: !this.state.showingTestPanel })}
                        >
                          <i className="fa fa-play"></i> {this.state.showingTestPanel ? 'Hide' : 'Test'}
                        </Button>
                        <Button 
                          size="sm" 
                          color="info" 
                          onClick={() => this.setState({ showingFullPrompt: !this.state.showingFullPrompt })}
                        >
                          <i className="fa fa-file-text"></i> {this.state.showingFullPrompt ? 'Hide' : 'Show'} Full
                        </Button>
                      </div>
                    ) : null
                  })()
                )}
              </Col>
            </Row>

            {!this.state.showingFullPrompt && !this.state.showingTestPanel && !this.state.showingTemplateManager && (
              <Row className="mb-3">
                <Col md={6}>
                  <label className="form-label">Template Management:</label>
                  {this.state.editableTemplates.length > 1 && (
                    <DropdownComponent className="sequences"
                      data={this.state.editableTemplates
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((template: EditablePromptTemplate) => ({
                          description: `Sequence ${template.sequence}${template.id === 0 ? ' (New)' : ''}${template.changedFields.length > 0 ? ' (Modified)' : ''}`,
                          key: template.sequence
                        }))
                      }
                      selected={this.state.selectedSequence}
                      onChange={(e: OnChangeEvent) => this.setState({ 
                        selectedSequence: e.selected.key as number
                      })}
                    />
                  )}
                  {this.state.editableTemplates.length <= 1 && (
                    <div className="text-muted">
                      {this.state.editableTemplates.length === 1 ? 'Single template' : 'No templates'}
                    </div>
                  )}
                </Col>
                <Col md={6} className="text-right">
                  {this.state.editableTemplates.length > 1 && (
                    <Button 
                      size="sm" 
                      color="secondary" 
                      outline
                      className="me-2"
                      onClick={() => this.setState({ showingTemplateManager: true })}
                      title="Reorder templates using drag and drop"
                    >
                      <i className="fa fa-sort"></i> Manage Order
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    color="primary" 
                    onClick={this.addNewTemplate}
                    disabled={this.state.editingTemplate && this.state.editableTemplates.some(t => t.changedFields.length > 0)}
                    title={this.state.editingTemplate && this.state.editableTemplates.some(t => t.changedFields.length > 0) ? 'Save changes before adding new template' : 'Add a new template'}
                  >
                    <i className="fa fa-plus"></i> Add New Template
                  </Button>
                </Col>
              </Row>
            )}

            {!this.state.showingFullPrompt && !this.state.showingTestPanel && this.state.showingTemplateManager && (
              <Row className="mb-3">
                <Col md={12}>
                  <div className="template-manager-section">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">
                        <i className="fa fa-sort me-2"></i>
                        Reorder Templates
                      </h5>
                      <Button 
                        color="secondary" 
                        size="sm"
                        onClick={() => this.setState({ showingTemplateManager: false })}
                      >
                        <i className="fa fa-times"></i> Done
                      </Button>
                    </div>
                    <DraggableTemplateList
                      templates={this.state.editableTemplates}
                      selectedSequence={this.state.selectedSequence}
                      onSelectTemplate={(sequence) => this.setState({ selectedSequence: sequence })}
                      onEditTemplate={(template) => {
                        this.setState({ 
                          selectedSequence: template.sequence,
                          editingTemplate: true,
                          showingTemplateManager: false 
                        })
                      }}
                      onSaveTemplate={this.saveTemplate}
                      onDeleteTemplate={this.deleteTemplate}
                      onReorderTemplates={this.handleTemplateReorder}
                      onAddNewTemplate={this.addNewTemplate}
                      disabled={this.state.editingTemplate}
                    />
                  </div>
                </Col>
              </Row>
            )}

            {selectedEngine.templates && selectedEngine.templates.length === 0 && (
              <div className="text-center text-muted py-4">
                <i className="fa fa-info-circle"></i> No templates found for this engine.
              </div>
            )}

            {selectedEngine.templates && selectedEngine.templates.length > 0 && this.state.selectedSequence >= 0 && !this.state.showingFullPrompt && !this.state.showingTestPanel && !this.state.showingTemplateManager && (
              (() => {
                const selectedTemplate = this.state.editableTemplates.find(t => t.sequence === this.state.selectedSequence)
                if (!selectedTemplate) return null
                
                const hasChanges = selectedTemplate.changedFields.length > 0
                const hasErrors = selectedTemplate.invalidFields.length > 0
                const isNew = selectedTemplate.id === 0
                
                return (
                  <Card>
                    <CardHeader>
                      <Row>
                        <Col md={8}>
                          <strong>
                            Template {selectedTemplate.sequence}
                            {isNew && <span className="badge badge-info ml-2">New</span>}
                            {hasChanges && <span className="badge badge-warning ml-2">Modified</span>}
                          </strong>
                        </Col>
                        <Col md={4} className="text-right">
                          {this.state.editingTemplate && (
                            <div className="btn-group btn-group-sm">
                              {hasChanges && (
                                <Button 
                                  size="sm" 
                                  color="success" 
                                  onClick={() => this.saveTemplate(selectedTemplate)}
                                  disabled={hasErrors || selectedTemplate.saveState === 'saving'}
                                >
                                  {selectedTemplate.saveState === 'saving' ? (
                                    <>
                                      <i className="fa fa-spinner fa-spin"></i> Saving...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa fa-save"></i> Save
                                    </>
                                  )}
                                </Button>
                              )}
                              {selectedTemplate.saveState === 'saved' && (
                                <Button size="sm" color="success" disabled>
                                  <i className="fa fa-check"></i> Saved
                                </Button>
                              )}
                              {selectedTemplate.saveState === 'error' && (
                                <Button 
                                  size="sm" 
                                  color="danger" 
                                  onClick={() => this.saveTemplate(selectedTemplate)}
                                  title={selectedTemplate.saveError}
                                >
                                  <i className="fa fa-retry"></i> Retry
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                color="danger" 
                                outline
                                onClick={() => this.deleteTemplate(selectedTemplate)}
                                title="Delete template"
                              >
                                <i className="fa fa-trash"></i>
                              </Button>
                            </div>
                          )}
                        </Col>
                      </Row>
                      {selectedTemplate.saveError && (
                        <Row className="mt-2">
                          <Col>
                            <div className="alert alert-danger py-1 mb-0">
                              <small><i className="fa fa-exclamation-triangle"></i> {selectedTemplate.saveError}</small>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col md={4} className="mb-3">
                          <label>Options (JSON):</label>
                          <PromptTemplateEditor
                            template={selectedTemplate}
                            onChange={(updatedTemplate) => this.updateTemplate(selectedTemplate.id, 'options', updatedTemplate.options)}
                            readOnly={!this.state.editingTemplate}
                            field="options"
                          />
                        </Col>
                        <Col md={8}>
                          <label>Prompt Template:</label>
                          <PromptTemplateEditor
                            template={selectedTemplate}
                            onChange={(updatedTemplate) => this.updateTemplate(selectedTemplate.id, 'prompt', updatedTemplate.prompt)}
                            readOnly={!this.state.editingTemplate}
                            field="prompt"
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )
              })()
            )}

            {this.state.showingFullPrompt && (
              <Card className="mt-3">
                <CardHeader>
                  <Row>
                    <Col>
                      <strong>Full Conversation Preview ({selectedEngine.templates && selectedEngine.templates.length || 0} steps)</strong>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    padding: '12px',
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: '14px',
                    whiteSpace: 'pre-wrap',
                    color: '#495057',
                    minHeight: '150px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {this.renderFullConversation(selectedEngine.templates || [])}
                  </div>
                </CardBody>
              </Card>
            )}

            {this.state.showingTestPanel && (
              <Card className="mt-3">
                <CardHeader>
                  <Row>
                    <Col md={8}>
                      <strong>Test Prompt Templates</strong>
                      <small className="text-muted ml-2">
                        Test your prompts with the /ai/{this.state.identifier}/{selectedPrompt.name} endpoint
                      </small>
                    </Col>
                    <Col md={4} className="text-right">
                      <Button 
                        size="sm" 
                        color="success" 
                        onClick={this.testPrompt}
                        disabled={this.state.testLoading || !this.state.testPayload}
                      >
                        {this.state.testLoading ? (
                          <>
                            <i className="fa fa-spinner fa-spin"></i> Testing...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-play"></i> Run Test
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      {(() => {
                        const payloadAnalysis = this.getPayloadAnalysis()
                        
                        if (!payloadAnalysis) {
                          return (
                            <div className="alert alert-info">
                              <i className="fa fa-info-circle"></i> No templates available for analysis.
                            </div>
                          )
                        }

                        return (
                          <div className="payload-analysis">
                            <h6 className="mb-3">Expected Payload Structure</h6>
                            
                            {payloadAnalysis.tokens.length > 0 && (
                              <div className="alert alert-primary py-2 mb-3">
                                <small>
                                  <strong>Detected Tokens:</strong> {payloadAnalysis.tokens.join(', ')}
                                  {payloadAnalysis.repeatSections.length > 0 && (
                                    <><br /><strong>Repeat Sections:</strong> {payloadAnalysis.repeatSections.join(', ')}</>
                                  )}
                                </small>
                              </div>
                            )}

                            {payloadAnalysis.schemaInfo && (
                              <div className="alert alert-info py-2 mb-3">
                                <small>
                                  <i className="fa fa-info-circle"></i> <strong>Nunjucks Templates with Schema:</strong> Found schema definitions for context objects
                                </small>
                              </div>
                            )}


                            {payloadAnalysis.schemaInfo && (
                              <div className="card mb-3">
                                <div className="card-header">
                                  <h6 className="mb-0">
                                    <i className="fa fa-cogs"></i> Expected Context Objects (Nunjucks)
                                  </h6>
                                </div>
                                <div className="card-body">
                                  {Object.entries(payloadAnalysis.schemaInfo).map(([sequence, schema]) => (
                                    <div key={sequence} className="mb-3">
                                      <label className="text-muted mb-2">
                                        <strong>Template Sequence {sequence}:</strong>
                                      </label>
                                      <pre className="mb-0" style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '1rem',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
                                      }}>
                                        {JSON.stringify(schema, null, 2)}
                                      </pre>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <small className="text-muted">
                                <i className="fa fa-lightbulb-o"></i> This structure shows the expected shape of your test payload based on the tokens, repeat sections, and schema definitions found in your prompt templates.
                              </small>
                            </div>
                          </div>
                        )
                      })()}
                      
                      {this.getCurrentTemplateType() === 'token' && (
                        <div className="payload-builder-section mt-4">
                          <h6 className="mb-3">Build Test Payload</h6>
                          
                          <div className="mb-3">
                            <label className="form-label">Choose Payload Type:</label>
                            <Input
                              type="select"
                              value={this.state.payloadBuilderMode}
                              onChange={(e) => this.handlePayloadModeChange(e.target.value)}
                            >
                              <option value="">Select payload type...</option>
                              <option value="article-analysis">Article Analysis</option>
                              <option value="content-creation">Content Creation</option>
                              <option value="email-campaign">Email Campaign</option>
                              <option value="rss-articles">RSS Articles (Array)</option>
                              <option value="category-keywords">Category Keywords (Array)</option>
                              <option value="background-colors">Background Colors (Object)</option>
                              <option value="custom">Custom (Manual Entry)</option>
                            </Input>
                          </div>

                          {/* Always show Expected Structure and Editable Payload when a mode is selected */}
                          {this.state.payloadBuilderMode && (
                            <div className="payload-editor-section">
                              {/* Expected Structure (Read-only) */}
                              {(() => {
                                const payloadAnalysis = this.getPayloadAnalysis()
                                if (!payloadAnalysis) return null
                                
                                return (
                                  <div className="card mb-3">
                                    <div className="card-header">
                                      <h6 className="mb-0">
                                        <i className="fa fa-code"></i> Expected Structure
                                      </h6>
                                    </div>
                                    <div className="card-body">
                                      <pre className="mb-0" style={{
                                        backgroundColor: '#f8f9fa',
                                        padding: '1rem',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
                                      }}>
                                        {JSON.stringify(payloadAnalysis.payloadStructure, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                )
                              })()}

                              {/* Editable Payload */}
                              <div className="card mb-3">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0">
                                    <i className="fa fa-edit"></i> Test Payload
                                  </h6>
                                  <button 
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={this.resetPayload}
                                    title="Reset to default values for this payload type"
                                  >
                                    <i className="fa fa-refresh"></i> Reset
                                  </button>
                                </div>
                                <div className="card-body">
                                  <Input
                                    type="textarea"
                                    rows={12}
                                    value={this.state.testPayload ? JSON.stringify(this.state.testPayload, null, 2) : '{}'}
                                    onChange={(e) => this.handlePayloadEdit(e.target.value)}
                                    style={{
                                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                      fontSize: '14px',
                                      border: 'none',
                                      resize: 'vertical'
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Status message */}
                              {this.state.payloadBuilderMode !== 'custom' && (
                                <div className="alert alert-success py-2 mb-3">
                                  <small>
                                    <i className="fa fa-check-circle"></i> <strong>Template loaded:</strong> {this.getPayloadModeDescription(this.state.payloadBuilderMode)}
                                  </small>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <ContextBuilder
                        context={this.state.testContext}
                        onChange={this.handleContextChange}
                        visible={this.getCurrentTemplateType() === 'nunjucks'}
                      />
                      
                    </Col>
                    <Col md={6}>
                      <h6 className="text-muted mb-3">Test Results</h6>
                      {this.state.testError && (
                        <div className="alert alert-danger">
                          <i className="fa fa-exclamation-triangle"></i> {this.state.testError}
                        </div>
                      )}
                      {this.state.testResponse && (
                        <div>
                          <div className="alert alert-success mb-2">
                            <i className="fa fa-check-circle"></i> Test completed successfully
                            {this.state.testResponse._metadata && (
                              <div className="mt-2">
                                <small>
                                  <strong>Template Type:</strong> {this.state.testResponse._metadata.templateType || 'unknown'} |{' '}
                                  <strong>Time:</strong> {new Date(this.state.testResponse._metadata.timestamp).toLocaleTimeString()}
                                  {this.state.testResponse._metadata.hasContext && (
                                    <span> | <i className="fa fa-code"></i> Nunjucks Context</span>
                                  )}
                                  {this.state.testResponse._metadata.tokenCount > 0 && (
                                    <span> | <i className="fa fa-tags"></i> {this.state.testResponse._metadata.tokenCount} Tokens</span>
                                  )}
                                </small>
                              </div>
                            )}
                          </div>
                          <div style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '12px',
                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                            fontSize: '14px',
                            whiteSpace: 'pre-wrap',
                            color: '#495057',
                            maxHeight: '400px',
                            overflowY: 'auto'
                          }}>
                            {JSON.stringify(this.state.testResponse, null, 2)}
                          </div>
                        </div>
                      )}
                      {!this.state.testResponse && !this.state.testError && !this.state.testLoading && (
                        <div className="text-center text-muted py-5">
                          <i className="fa fa-flask fa-3x mb-3"></i>
                          <p>Configure your test payload and click "Run Test" to see results here.</p>
                        </div>
                      )}
                      
                      {this.state.testHistory.length > 0 && (
                        <div className="mt-4">
                          <h6 className="text-muted mb-3">
                            <i className="fa fa-history"></i> Test History ({this.state.testHistory.length})
                          </h6>
                          <div className="test-history" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {this.state.testHistory.map((entry, index) => (
                              <div key={entry.id} className={`alert ${entry.error ? 'alert-danger' : 'alert-success'} py-2 mb-2`}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <small>
                                      <strong>{new Date(entry.timestamp).toLocaleTimeString()}</strong> | 
                                      {entry.templateType && <span> {entry.templateType}</span>}
                                      {entry.context && <span> | <i className="fa fa-code"></i></span>}
                                      {entry.tokens && entry.tokens.length > 0 && <span> | <i className="fa fa-tags"></i> {entry.tokens.length}</span>}
                                    </small>
                                    <div className="mt-1">
                                      <small className="text-truncate d-block" style={{ maxWidth: '300px' }}>
                                        <strong>Prompt:</strong> {entry.payload.prompt}
                                      </small>
                                      {entry.error && (
                                        <small className="text-danger">
                                          <i className="fa fa-exclamation-triangle"></i> {entry.error}
                                        </small>
                                      )}
                                      {entry.response && (
                                        <small className="text-success">
                                          <i className="fa fa-check-circle"></i> Success
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    color="link" 
                                    className="p-0 ml-2"
                                    onClick={() => {
                                      this.setState({
                                        testPayload: entry.payload,
                                        testContext: entry.context || {},
                                        testTokens: entry.tokens || [],
                                        testResponse: entry.response || null,
                                        testError: entry.error || null
                                      })
                                    }}
                                    title="Load this test configuration"
                                  >
                                    <i className="fa fa-undo"></i>
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ 
              selectedPrompt: -1, 
              selectedEngine: -1, 
              selectedSequence: -1,
              editableTemplates: [],  
              editingTemplates: false,
              editingTemplate: false,
              showingFullPrompt: false,
              showingTestPanel: false,
              showingTemplateManager: false,
              testPayload: null,
              testContext: {},
              testTokens: [],
              testResponse: null,
              testError: null,
              testLoading: false,
              payloadBuilderMode: '',
              customPayload: {}
            })}>
              <i className="fa fa-times"></i> Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>

  }


  private savePrompt = () => {
    const activeEngines: any[] = this.state.editPrompt.engines.map((engine) => ({
      aiEngineId: engine.id,
      isDefault: engine.id === this.state.editPrompt.defaultEngine.id,
      isActive: true,
    }))
    const activeEngineIds = activeEngines.map((e) => e.aiEngineId)

    const inactiveEngines: any[] = this.state.promptTypes.find((p: PromptType) => p.id === this.state.editPrompt.id)
      .engines.filter((engine) => !activeEngineIds.includes(engine.id))
      .map((engine) => ({
      aiEngineId: engine.id,
      isActive: false,
    }))

    return this.context.entityMetadata
     .getEntityObject('aiPromptType')
     .then((entity) => {
       entity.setRecordId(this.state.communityId, this.state.editPrompt.id)
       entity.data.engines = activeEngines.concat(inactiveEngines)
       return entity.save().then(() => {
          this.setState({
            editPrompt: NO_PROMPT_TYPE
          })
       })
     })
  }

  private isNunjucksTemplate = (template: PromptTemplate): boolean => {
    try {
      const options = JSON.parse(template.options || '{}')
      return options.nunjucks === true
    } catch (e) {
      return false
    }
  }

  private getCurrentTemplateType = (): 'nunjucks' | 'token' | null => {
    const selectedPrompt = this.state.promptTypes.find(p => p.id === this.state.selectedPrompt)
    if (!selectedPrompt) return null
    
    const selectedEngine = selectedPrompt.engines.find(e => e.id === this.state.selectedEngine)
    if (!selectedEngine || !selectedEngine.templates || selectedEngine.templates.length === 0) return null
    
    const selectedTemplate = selectedEngine.templates.find(t => t.sequence === this.state.selectedSequence)
    if (!selectedTemplate) return null
    
    return this.isNunjucksTemplate(selectedTemplate) ? 'nunjucks' : 'token'
  }

  // Token analysis and payload field detection
  private analyzeConversationTokens = (conversationPreview: string): {
    tokens: Set<string>,
    repeatSections: Set<string>,
    requiredFields: Set<string>
  } => {
    const tokens = new Set<string>()
    const repeatSections = new Set<string>()
    const requiredFields = new Set<string>()

    // Extract all tokens (__TOKEN__)
    const tokenMatches = conversationPreview.match(/__[A-Z0-9_]+__/g)
    if (tokenMatches) {
      tokenMatches.forEach(token => tokens.add(token))
    }

    // Extract repeat sections
    const repeatMatches = conversationPreview.match(/REPEAT: (\w+)/g)
    if (repeatMatches) {
      repeatMatches.forEach(match => {
        const repeatKey = match.replace('REPEAT: ', '')
        repeatSections.add(repeatKey)
      })
    }

    // Map tokens to required payload fields
    this.mapTokensToPayloadFields(tokens, repeatSections, requiredFields)

    return { tokens, repeatSections, requiredFields }
  }

  private mapTokensToPayloadFields = (
    tokens: Set<string>, 
    repeatSections: Set<string>, 
    requiredFields: Set<string>
  ) => {
    // Always include basic fields if any tokens are present
    if (tokens.size > 0) {
      requiredFields.add('prompt')
      requiredFields.add('tone')
      requiredFields.add('language')
    }

    // Map individual tokens to fields
    tokens.forEach(token => {
      switch (token) {
        case '__WORD_COUNT__':
          requiredFields.add('length')
          break
        case '__TITLE__':
          requiredFields.add('title')
          break
        case '__TEXT__':
        case '__CONTENT__':
          requiredFields.add('content')
          break
        case '__URL__':
          requiredFields.add('url')
          break
        case '__TYPE__':
          requiredFields.add('promptType')
          break
        case '__TONE__':
          requiredFields.add('tone')
          break
        case '__COUNT__':
        case '__COUNT_2__':
          // These represent array lengths - we'll determine which arrays based on repeat sections
          // For now, mark that we need array fields
          break
        case '__KEYWORDS__':
          requiredFields.add('topics')
          break
        case '__COLOR_1__':
        case '__COLOR_2__':
          requiredFields.add('background')
          break
        case '__ARTICLES__':
          requiredFields.add('articles')
          break
      }
    })

    // Map repeat sections to array fields
    repeatSections.forEach(repeatKey => {
      switch (repeatKey) {
        case 'article':
        case 'articles':
          requiredFields.add('articles')
          break
        case 'category':
        case 'categories':
          requiredFields.add('categories')
          break
        case 'subject':
        case 'subjects':
          // Subject lines need their own array structure
          requiredFields.add('subjects')
          break
        case 'topics':
          requiredFields.add('topics')
          break
        case 'colors':
        case 'background':
          requiredFields.add('background')
          break
        case 'emojis':
          requiredFields.add('emojis')
          break
        case 'language':
          requiredFields.add('language')
          break
      }
    })
  }

  private getPayloadAnalysis = () => {
    const selectedPrompt = this.state.promptTypes.filter((p: PromptType) => p.id === this.state.selectedPrompt)[0]
    if (!selectedPrompt) {
      return null
    }

    const selectedEngine = selectedPrompt.engines.filter((e: AIEngine) => e.id === this.state.selectedEngine)[0]
    if (!selectedEngine || !selectedEngine.templates) {
      return null
    }

    const conversationPreview = this.renderFullConversation(selectedEngine.templates)
    const analysis = this.analyzeConversationTokens(conversationPreview)

    // Build expected payload structure based on tokens and repeats
    const payloadStructure = this.buildExpectedPayloadStructure(analysis.tokens, analysis.repeatSections)

    // Extract schema information from Nunjucks templates
    const schemaInfo = this.extractSchemaFromTemplates(selectedEngine.templates)

    return {
      tokens: Array.from(analysis.tokens).sort(),
      repeatSections: Array.from(analysis.repeatSections).sort(),
      payloadStructure,
      schemaInfo
    }
  }

  private buildExpectedPayloadStructure = (tokens: Set<string>, repeatSections: Set<string>) => {
    const structure: any = {}

    // Map tokens to payload fields
    tokens.forEach(token => {
      switch (token) {
        case '__TITLE__':
          structure.title = 'string'
          break
        case '__TEXT__':
        case '__CONTENT__':
          structure.content = 'string'
          break
        case '__WORD_COUNT__':
          structure.length = 'number'
          break
        case '__TONE__':
          structure.tone = 'string'
          break
        case '__URL__':
          structure.url = 'string'
          break
        case '__COUNT__':
        case '__COUNT_2__':
          // These represent array lengths - will be determined by repeat sections
          break
        case '__KEYWORDS__':
          structure.topics = 'string[]'
          break
        case '__COLOR_1__':
        case '__COLOR_2__':
          if (!structure.background) {
            structure.background = {
              colors: {
                primary: 'string',
                secondary: 'string'
              }
            }
          }
          break
      }
    })

    // Map repeat sections to arrays
    repeatSections.forEach(repeatKey => {
      switch (repeatKey) {
        case 'article':
        case 'articles':
          structure.articles = [{ title: 'string', text: 'string' }]
          break
        case 'subject':
        case 'subjects':
          structure.subjects = [{ title: 'string' }]
          break
        case 'category':
        case 'categories':
          structure.categories = [{ name: 'string', keywords: 'string' }]
          break
        case 'background':
          if (!structure.background) {
            structure.background = { summary: 'string' }
          }
          break
      }
    })

    return structure
  }

  private extractSchemaFromTemplates = (templates: PromptTemplate[]) => {
    const schemas: { [templateSequence: number]: any } = {}
    let hasSchemas = false

    templates.forEach(template => {
      if (this.isNunjucksTemplate(template)) {
        try {
          const options = JSON.parse(template.options || '{}')
          if (options.schema) {
            schemas[template.sequence] = options.schema
            hasSchemas = true
          }
        } catch (e) {
          // Invalid JSON in options, skip
        }
      }
    })

    return hasSchemas ? schemas : null
  }

  private handleContextChange = (context: any) => {
    this.setState({ testContext: context })
  }

  private handlePayloadModeChange = (mode: string) => {
    this.setState({ payloadBuilderMode: mode })
    
    if (mode && mode !== 'custom') {
      const payload = this.getPresetPayload(mode)
      this.setState({ testPayload: payload })
    } else if (mode === 'custom') {
      // Initialize custom payload with base structure converted to actual payload
      const payloadAnalysis = this.getPayloadAnalysis()
      if (payloadAnalysis) {
        const basePayload = this.convertStructureToPayload(payloadAnalysis.payloadStructure)
        this.setState({ testPayload: basePayload })
      }
    }
  }

  private getPresetPayload = (mode: string): ChatPayload => {
    const basePayload: ChatPayload = {
      aiPromptType: '',
      emojis: 0,
      language: 'English',
      length: 100,
      prompt: 'Please process the provided content and generate a response',
      promptType: '',
      tone: 'professional'
    }

    switch (mode) {
      case 'article-analysis':
        return {
          ...basePayload,
          prompt: 'Please analyze the following article and provide key insights',
          title: 'AI in Business: A Comprehensive Overview',
          content: 'Artificial intelligence is transforming the way businesses operate...',
          tone: 'professional',
          length: 150
        }
      
      case 'content-creation':
        return {
          ...basePayload,
          prompt: 'Create engaging content for our technology blog',
          tone: 'conversational',
          length: 800,
          topics: ['innovation', 'efficiency', 'ROI']
        }
      
      case 'email-campaign':
        return {
          ...basePayload,
          prompt: 'Create a friendly email for our healthcare announcement',
          tone: 'friendly',
          content: 'New product announcement details...'
        }
      
      case 'rss-articles':
        return {
          ...basePayload,
          prompt: 'Process these RSS articles and generate relevant content',
          articles: [{
            title: 'AI Revolution in Healthcare',
            text: 'Artificial intelligence is transforming medical diagnosis...'
          }]
        }
      
      case 'category-keywords':
        return {
          ...basePayload,
          prompt: 'Generate content based on these category keywords',
          topics: ['AI', 'machine learning', 'automation', 'innovation']
        }
      
      case 'background-colors':
        return {
          ...basePayload,
          prompt: 'Create branded content using our company colors and background',
          background: {
            summary: 'Tech startup focused on innovation',
            colors: {
              primary: '#007bff',
              secondary: '#6c757d'
            }
          }
        }
      
      default:
        return basePayload
    }
  }

  private getPayloadModeDescription = (mode: string): string => {
    switch (mode) {
      case 'article-analysis': return 'Professional article analysis with title and content'
      case 'content-creation': return 'Blog content creation with keywords and conversational tone'
      case 'email-campaign': return 'Friendly email campaign with healthcare focus'
      case 'rss-articles': return 'RSS feed template with article array structure'
      case 'category-keywords': return 'Category-based template with keyword arrays'
      case 'background-colors': return 'Template with background colors and company context'
      default: return 'Unknown payload type'
    }
  }

  private handlePayloadEdit = (jsonString: string) => {
    try {
      const parsed = JSON.parse(jsonString)
      this.setState({ testPayload: parsed })
    } catch (e) {
      // Invalid JSON, keep current state for now
      // Could add validation feedback here later
    }
  }

  private resetPayload = () => {
    const mode = this.state.payloadBuilderMode
    if (mode && mode !== 'custom') {
      // Reset to the original preset payload
      const payload = this.getPresetPayload(mode)
      this.setState({ testPayload: payload })
    } else if (mode === 'custom') {
      // Reset to base structure for custom mode
      const payloadAnalysis = this.getPayloadAnalysis()
      if (payloadAnalysis) {
        const basePayload = this.convertStructureToPayload(payloadAnalysis.payloadStructure)
        this.setState({ testPayload: basePayload })
      }
    }
  }

  private convertStructureToPayload = (structure: any): any => {
    const payload: any = {}
    
    Object.keys(structure).forEach(key => {
      const value = structure[key]
      if (value === 'string') {
        // Provide meaningful defaults for common fields
        if (key === 'prompt') {
          payload[key] = 'Please process the provided content and generate a response'
        } else if (key === 'tone') {
          payload[key] = 'professional'
        } else if (key === 'language') {
          payload[key] = 'English'
        } else {
          payload[key] = ''
        }
      } else if (value === 'number') {
        // Provide meaningful defaults for numeric fields
        if (key === 'length') {
          payload[key] = 100
        } else {
          payload[key] = 0
        }
      } else if (value === 'boolean') {
        payload[key] = false
      } else if (Array.isArray(value)) {
        payload[key] = [this.convertStructureToPayload(value[0])]
      } else if (typeof value === 'object') {
        payload[key] = this.convertStructureToPayload(value)
      } else {
        payload[key] = value
      }
    })
    
    return payload
  }

  private addToTestHistory = (entry: Omit<TestHistoryEntry, 'id' | 'timestamp'>) => {
    const historyEntry: TestHistoryEntry = {
      ...entry,
      id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    }

    this.setState(prevState => ({
      testHistory: [historyEntry, ...prevState.testHistory.slice(0, 4)] // Keep last 5 entries
    }))
  }

  private getExistingSequences = (): number[] => {
    return this.state.editableTemplates.map(t => t.sequence)
  }

  private getNextSequence = (): number => {
    const sequences = this.getExistingSequences()
    return sequences.length > 0 ? Math.max(...sequences) + 1 : 1
  }

  private createNewTemplate = (): EditablePromptTemplate => {
    return {
      id: 0, // Indicates new template
      sequence: this.getNextSequence(),
      prompt: '',
      options: '{"role": "user"}',
      changedFields: ['prompt', 'options', 'sequence'],
      invalidFields: [],
      saveState: 'idle',
      saveError: undefined
    }
  }

  private addNewTemplate = () => {
    const newTemplate = this.createNewTemplate()
    this.setState(prevState => ({
      editableTemplates: [...prevState.editableTemplates, newTemplate],
      selectedSequence: newTemplate.sequence
    }))
  }

  private updateTemplate = (templateId: number, field: keyof EditablePromptTemplate, value: any) => {
    this.setState(prevState => ({
      editableTemplates: prevState.editableTemplates.map(template => {
        if (template.id === templateId || (templateId === 0 && template.sequence === this.state.selectedSequence)) {
          const updatedTemplate = { ...template, [field]: value }
          
          // Add to changed fields if not already present
          if (!updatedTemplate.changedFields.includes(field)) {
            updatedTemplate.changedFields = [...updatedTemplate.changedFields, field]
          }
          
          // Reset save state when changes are made
          updatedTemplate.saveState = 'idle'
          updatedTemplate.saveError = undefined
          
          return updatedTemplate
        }
        return template
      })
    }))
  }

  private saveTemplate = async (template: EditablePromptTemplate): Promise<void> => {
    // Set saving state
    this.setState(prevState => ({
      editableTemplates: prevState.editableTemplates.map(t => 
        t.id === template.id || (template.id === 0 && t.sequence === template.sequence)
          ? { ...t, saveState: 'saving', saveError: undefined }
          : t
      )
    }))

    try {
      const isNew = template.id === 0
      const entityName = 'aiPromptTemplate'
      
      const entity = await this.context.entityMetadata.getEntityObject(
        entityName, 
        this.state.communityId, 
        isNew ? null : template.id
      )
      
      // Set the data fields
      entity.data.prompt = template.prompt
      entity.data.options = template.options
      entity.data.sequence = template.sequence
      
      if (isNew) {
        // For new templates, we need to associate with the current prompt type and engine
        const selectedPrompt = this.state.promptTypes.find(p => p.id === this.state.selectedPrompt)
        const selectedEngine = selectedPrompt ? selectedPrompt.engines.find(e => e.id === this.state.selectedEngine) : undefined
        
        if (selectedEngine) {
          entity.data.ai_engine_id = selectedEngine.id
        }
      }
      
      const savedEntity = await entity.save()
      
      // Update the template with the saved data and reset state
      this.setState(prevState => ({
        editableTemplates: prevState.editableTemplates.map(t => {
          if (t.id === template.id || (template.id === 0 && t.sequence === template.sequence)) {
            return {
              ...t,
              id: savedEntity.id || t.id, // Update ID for new templates
              changedFields: [],
              invalidFields: [],
              saveState: 'saved',
              saveError: undefined
            }
          }
          return t
        })
      }))
      
      // Clear saved state after a delay
      setTimeout(() => {
        this.setState(prevState => ({
          editableTemplates: prevState.editableTemplates.map(t => 
            t.id === (savedEntity.id || template.id)
              ? { ...t, saveState: 'idle' }
              : t
          )
        }))
      }, 2000)
      
    } catch (error) {
      // Handle save error
      
      this.setState(prevState => ({
        editableTemplates: prevState.editableTemplates.map(t => 
          t.id === template.id || (template.id === 0 && t.sequence === template.sequence)
            ? { 
                ...t, 
                saveState: 'error', 
                saveError: error instanceof Error ? error.message : 'Failed to save template'
              }
            : t
        )
      }))
    }
  }

  private handleTemplateReorder = (reorderedTemplates: EditablePromptTemplate[]) => {
    // Update the state with reordered templates
    this.setState({
      editableTemplates: reorderedTemplates
    })
    
    // Batch save all templates that have sequence changes
    const templatesWithSequenceChanges = reorderedTemplates.filter(t => 
      t.changedFields.includes('sequence')
    )
    
    // Save sequence changes to backend (debounced to avoid excessive API calls)
    if (templatesWithSequenceChanges.length > 0) {
      this.batchUpdateSequences(templatesWithSequenceChanges)
    }
  }

  private batchUpdateSequences = async (templates: EditablePromptTemplate[]) => {
    const entityName = 'aiPromptTemplate'
    
    try {
      // Update sequences for all changed templates
      const updatePromises = templates.map(async (template) => {
        const entity = await this.context.entityMetadata.getEntityObject(
          entityName, this.state.communityId, template.id === 0 ? null : template.id
        )
        
        entity.data.sequence = template.sequence
        entity.data.prompt = template.prompt
        entity.data.options = template.options
        
        // Associate with current prompt type and engine
        const selectedPromptType = this.state.promptTypes.find(p => p.id === this.state.selectedPrompt)
        const selectedEngine = selectedPromptType ? selectedPromptType.engines.find(e => e.id === this.state.selectedEngine) : undefined
        
        if (selectedPromptType && selectedEngine) {
          entity.data.aiPromptTypeId = selectedPromptType.id
          entity.data.aiEngineId = selectedEngine.id
        }
        
        return entity.save()
      })
      
      await Promise.all(updatePromises)
      
      // Update state to reflect saved sequences
      this.setState(prevState => ({
        editableTemplates: prevState.editableTemplates.map(t => {
          if (templates.some(updatedTemplate => updatedTemplate.id === t.id || (t.id === 0 && updatedTemplate.sequence === t.sequence))) {
            return {
              ...t,
              changedFields: t.changedFields.filter(field => field !== 'sequence'),
              saveState: 'saved'
            }
          }
          return t
        })
      }))
      
      // Clear saved state after a delay
      setTimeout(() => {
        this.setState(prevState => ({
          editableTemplates: prevState.editableTemplates.map(t => 
            t.saveState === 'saved' ? { ...t, saveState: 'idle' } : t
          )
        }))
      }, 2000)
      
    } catch (error) {
      // Handle batch update error
      
      // Mark templates as having errors
      this.setState(prevState => ({
        editableTemplates: prevState.editableTemplates.map(t => {
          if (templates.some(updatedTemplate => updatedTemplate.id === t.id || (t.id === 0 && updatedTemplate.sequence === t.sequence))) {
            return {
              ...t,
              saveState: 'error',
              saveError: 'Failed to update sequence'
            }
          }
          return t
        })
      }))
    }
  }

  private deleteTemplate = async (template: EditablePromptTemplate): Promise<void> => {
    if (template.id === 0) {
      // For new unsaved templates, just remove from state
      this.setState(prevState => ({
        editableTemplates: prevState.editableTemplates.filter(t => t.sequence !== template.sequence),
        selectedSequence: prevState.editableTemplates.length > 1 
          ? (() => {
              const remaining = prevState.editableTemplates.find(t => t.sequence !== template.sequence)
              return remaining ? remaining.sequence : -1
            })()
          : -1
      }))
      return
    }

    try {
      const entityName = 'aiPromptTemplate'
      const entity = await this.context.entityMetadata.getEntityObject(
        entityName, 
        this.state.communityId, 
        template.id
      )
      
      await entity.delete()
      
      // Remove from state
      this.setState(prevState => ({
        editableTemplates: prevState.editableTemplates.filter(t => t.id !== template.id),
        selectedSequence: prevState.editableTemplates.length > 1 
          ? (() => {
              const remaining = prevState.editableTemplates.find(t => t.id !== template.id)
              return remaining ? remaining.sequence : -1
            })()
          : -1
      }))
      
    } catch (error) {
      // Handle delete error - could add error handling UI here
    }
  }

  private testPrompt = async () => {
    // Validation
    const validationResult = this.validateTestRequest()
    if (!validationResult.valid) {
      this.setState({ testError: validationResult.error })
      return
    }

    const selectedPrompt = this.state.promptTypes.find(p => p.id === this.state.selectedPrompt)!
    const templateType = this.getCurrentTemplateType()

    this.setState({ 
      testLoading: true, 
      testError: null, 
      testResponse: null 
    })

    try {
      // Build the request payload based on template type
      const requestPayload = this.buildTestRequestPayload(templateType)
      
      // Use the proper API infrastructure with the generative AI endpoint
      const url = `${AjaxWrapper.getServerUrl()}/ai/${this.state.communityId}/${selectedPrompt.name}`
      
      // Prepare for API call
      
      const response = await AjaxWrapper.ajax(url, HttpMethod.POST, requestPayload)
      
      // Format and validate response
      const formattedResponse = this.formatTestResponse(response)
      
      // Add to history
      this.addToTestHistory({
        payload: this.state.testPayload,
        context: templateType === 'nunjucks' ? this.state.testContext : undefined,
        tokens: templateType === 'token' ? this.state.testTokens : undefined,
        response: formattedResponse,
        templateType
      })
      
      this.setState({ 
        testResponse: formattedResponse,
        testLoading: false 
      })
      
    } catch (error) {
      // Handle test error
      
      let errorMessage = 'An error occurred during testing'
      
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (typeof error === 'object' && error !== null) {
        // Handle structured error responses
        if ('message' in error) {
          errorMessage = error.message as string
        } else if ('error' in error) {
          errorMessage = error.error as string
        } else {
          errorMessage = JSON.stringify(error)
        }
      }
      
      // Add failed test to history
      this.addToTestHistory({
        payload: this.state.testPayload,
        context: templateType === 'nunjucks' ? this.state.testContext : undefined,
        tokens: templateType === 'token' ? this.state.testTokens : undefined,
        error: errorMessage,
        templateType
      })
      
      this.setState({ 
        testError: errorMessage,
        testLoading: false 
      })
    }
  }

  private validateTestRequest = (): { valid: boolean; error?: string } => {
    if (!this.state.testPayload) {
      return { valid: false, error: 'Please configure the test payload first' }
    }

    const selectedPrompt = this.state.promptTypes.find(p => p.id === this.state.selectedPrompt)
    if (!selectedPrompt) {
      return { valid: false, error: 'No prompt type selected' }
    }

    if (this.state.communityId < 0) {
      return { valid: false, error: 'Community not initialized' }
    }

    // Validate payload required fields
    if (!this.state.testPayload.prompt || this.state.testPayload.prompt.trim().length === 0) {
      return { valid: false, error: 'User prompt is required' }
    }

    // Validate template-specific requirements
    const templateType = this.getCurrentTemplateType()
    if (templateType === 'nunjucks') {
      // For Nunjucks templates, context should be valid JSON if provided
      if (this.state.testContext && Object.keys(this.state.testContext).length > 0) {
        try {
          JSON.stringify(this.state.testContext)
        } catch (e) {
          return { valid: false, error: 'Invalid JSON context for Nunjucks template' }
        }
      }
    } else if (templateType === 'token') {
      // For token templates, validate token format
      const invalidTokens = this.state.testTokens.filter(token => 
        !token.key.startsWith('__') || 
        !token.key.endsWith('__') ||
        !/^__[A-Z0-9_]+__$/.test(token.key)
      )
      if (invalidTokens.length > 0) {
        return { valid: false, error: `Invalid token format: ${invalidTokens.map(t => t.key).join(', ')}` }
      }
    }

    return { valid: true }
  }

  private buildTestRequestPayload = (templateType: 'nunjucks' | 'token' | null): any => {
    const basePayload = { ...this.state.testPayload }

    if (templateType === 'nunjucks' && this.state.testContext && Object.keys(this.state.testContext).length > 0) {
      // For Nunjucks templates, include context
      return {
        ...basePayload,
        context: this.state.testContext
      }
    } else if (templateType === 'token' && this.state.testTokens.length > 0) {
      // For token templates, we could include tokens for server-side replacement
      // or apply them client-side before sending
      return {
        ...basePayload,
        tokens: this.state.testTokens.reduce((acc, token) => {
          acc[token.key] = token.value
          return acc
        }, {} as { [key: string]: string })
      }
    }

    return basePayload
  }

  private formatTestResponse = (response: any): any => {
    // Add metadata to response for better display
    return {
      ...response,
      _metadata: {
        timestamp: new Date().toISOString(),
        templateType: this.getCurrentTemplateType(),
        hasContext: this.getCurrentTemplateType() === 'nunjucks' && Object.keys(this.state.testContext).length > 0,
        tokenCount: this.getCurrentTemplateType() === 'token' ? this.state.testTokens.length : 0
      }
    }
  }

  private renderFullConversation = (templates: PromptTemplate[]): string => {
    if (!templates || templates.length === 0) {
      return 'No templates found.'
    }

    const sortedTemplates = templates.sort((a, b) => a.sequence - b.sequence)
    
    return sortedTemplates.map((template, index) => {
      try {
        const options = JSON.parse(template.options || '{}')
        const role = options.role || 'user'
        const repeat = options.repeat
        const prompt = template.prompt || ''
        
        let stepHeader = `--- Step ${index + 1} (Sequence ${template.sequence}) ---\nROLE: ${role.toUpperCase()}`
        
        if (repeat) {
          stepHeader += `\nREPEAT: ${repeat}`
        }
        
        return `${stepHeader}\n\nTEXT: ${prompt}`
      } catch (e) {
        return `--- Step ${index + 1} (Sequence ${template.sequence}) ---\nROLE: USER\n\nTEXT: ${template.prompt || ''}`
      }
    }).join('\n\n')
  }

}

export const RexPrompts = GenericRedux.createConnect(
  RexPromptsComponent,
  "rex_prompts",
)
